import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { Info, Minus, Move, Plus, RotateCcw } from "lucide-react";
import {
  EVENT_IMAGE_ZOOM_MAX,
  EVENT_IMAGE_ZOOM_MIN,
  clampEventImageZoom,
  stepEventImageZoom,
} from "../../utils/eventImageZoom";
import "./EventsManageTable.css";

const API = process.env.REACT_APP_API_BASE;
const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
].filter(Boolean);

// ─── Image Position Editor (drag-to-reposition) ───────────────────────────────

const ImagePositionEditor = ({ eventId, coverImage }) => {
  const [pos, setPos] = useState({ x: 50, y: 50, zoom: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });

  // Load saved position from subcollection
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/event/imagePosition/get?id=${eventId}`)
      .then((r) => {
        if (r.data) {
          setPos({
            x: r.data.x ?? 50,
            y: r.data.y ?? 50,
            zoom: clampEventImageZoom(r.data.zoom),
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  const savePosition = useCallback(
    async (x, y, zoom) => {
      setSaving(true);
      setSaved(false);
      try {
        await axios.put(
          `${API}/event/imagePosition/save?id=${eventId}`,
          { x, y, zoom: clampEventImageZoom(zoom) },
          { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        );
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch {
        toast.error("Eroare la salvarea poziției imaginii");
      } finally {
        setSaving(false);
      }
    },
    [eventId]
  );

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    dragRef.current && dragRef.current.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDragging.current || !dragRef.current) return;
    const rect = dragRef.current.getBoundingClientRect();
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };

    // Convert pixel delta to percentage of container
    const dxPct = (dx / rect.width) * 100;
    const dyPct = (dy / rect.height) * 100;

    setPos((prev) => ({
      x: Math.max(0, Math.min(100, prev.x - dxPct)),
      y: Math.max(0, Math.min(100, prev.y - dyPct)),
      zoom: prev.zoom,
    }));
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      if (!isDragging.current) return;
      isDragging.current = false;
      setPos((prev) => {
        savePosition(
          Math.round(prev.x * 10) / 10,
          Math.round(prev.y * 10) / 10,
          prev.zoom
        );
        return prev;
      });
    },
    [savePosition]
  );

  const updateZoom = useCallback(
    (direction) => {
      setPos((current) => {
        const zoom = stepEventImageZoom(current.zoom, direction);
        savePosition(current.x, current.y, zoom);
        return { ...current, zoom };
      });
    },
    [savePosition]
  );

  const resetImage = useCallback(() => {
    const nextPosition = { x: 50, y: 50, zoom: 1 };
    setPos(nextPosition);
    savePosition(nextPosition.x, nextPosition.y, nextPosition.zoom);
  }, [savePosition]);

  if (!coverImage) {
    return (
      <p className="evt-pos__no-image">Evenimentul nu are o imagine de copertă.</p>
    );
  }

  return (
    <div className="evt-pos">
      <p className="evt-pos__hint">
        Trage imaginea pentru a ajusta poziția vizibilă. Se salvează automat la eliberare.
      </p>
      <div
        ref={dragRef}
        className="evt-pos__canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{ cursor: "grab" }}
      >
        {loading ? (
          <div className="evt-pos__spinner" />
        ) : (
          <img
            src={coverImage}
            alt="Copertă eveniment"
            className="evt-pos__img"
            style={{
              objectPosition: `${pos.x}% ${pos.y}%`,
              transform: `scale(${pos.zoom})`,
              transformOrigin: `${pos.x}% ${pos.y}%`,
            }}
            draggable="false"
          />
        )}
        <div className="evt-pos__crosshair" />
      </div>
      <div className="evt-pos__zoom-controls" aria-label="Control zoom imagine">
        <button
          type="button"
          className="evt-pos__zoom-button"
          onClick={() => updateZoom(-1)}
          disabled={pos.zoom <= EVENT_IMAGE_ZOOM_MIN || saving}
          aria-label="Micșorează imaginea"
        >
          <Minus size={16} aria-hidden="true" />
        </button>
        <output className="evt-pos__zoom-value" aria-live="polite">
          {Math.round(pos.zoom * 100)}%
        </output>
        <button
          type="button"
          className="evt-pos__zoom-button"
          onClick={() => updateZoom(1)}
          disabled={pos.zoom >= EVENT_IMAGE_ZOOM_MAX || saving}
          aria-label="Mărește imaginea"
        >
          <Plus size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="evt-pos__zoom-button evt-pos__zoom-button--reset"
          onClick={resetImage}
          disabled={saving}
          aria-label="Resetează poziția și zoomul"
        >
          <RotateCcw size={15} aria-hidden="true" />
        </button>
      </div>
      <div className="evt-pos__status">
        {saving && <span className="evt-pos__saving">Se salvează…</span>}
        {saved && <span className="evt-pos__saved">✓ Salvat</span>}
        {!saving && !saved && (
          <span className="evt-pos__coords">
            X: {Math.round(pos.x)}% · Y: {Math.round(pos.y)}% · Zoom: {Math.round(pos.zoom * 100)}%
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────

const EventsManageTable = () => {
  const email = useSelector((s) => s?.user?.user?.email);
  const isAdmin = !!email && ADMIN_EMAILS.includes(email);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repositioningId, setRepositioningId] = useState(null);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/campaign/get`);
      const data = Array.isArray(response.data) ? response.data : [];
      setEvents(data);
    } catch (err) {
      console.error("Error fetching events:", err);
      toast.error("Eroare la încărcarea evenimentelor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleToggleSkip = async (event) => {
    const id = event.id || event.docId;
    const newSkipped = !event.skipped;

    try {
      await axios.put(
        `${API}/campaign/put?id=${id}`,
        { skipped: newSkipped },
        { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
      );
      setEvents((prev) =>
        prev.map((e) =>
          (e.id || e.docId) === id ? { ...e, skipped: newSkipped } : e
        )
      );
      toast.success(
        newSkipped
          ? "Evenimentul a fost ascuns din pagină"
          : "Evenimentul este din nou vizibil"
      );
    } catch (err) {
      console.error("Error toggling skip:", err);
      toast.error("Eroare la actualizarea evenimentului");
    }
  };

  if (loading) {
    return <p className="events-table__loading">Se încarcă evenimentele...</p>;
  }

  if (events.length === 0) {
    return <p className="events-table__empty">Nu există evenimente create.</p>;
  }

  return (
    <div className="events-table">
      <h3 className="events-table__title">Gestionare Evenimente</h3>
      <p className="events-table__subtitle">
        Marchează evenimentele pe care dorești să le ascunzi.
      </p>
      {isAdmin && (
        <div className="events-table__admin-notice">
          <Info size={14} aria-hidden="true" />
          Admin: Coloana <strong>Imagine</strong> îți permite să repoziționezi imaginea de copertă prin drag &amp; drop. Modificările se salvează automat.
        </div>
      )}
      <div className="events-table__wrapper">
        <table className="events-table__table">
          <thead>
            <tr>
              <th>Titlu</th>
              <th>Tip</th>
              <th>Locație</th>
              <th>Perioadă</th>
              <th>Status</th>
              <th>Acțiune</th>
              {isAdmin && <th className="events-table__th--admin">Imagine</th>}
            </tr>
          </thead>
          <tbody>
            {events.map((event) => {
              const id = event.id || event.docId;
              const isSkipped = !!event.skipped;
              const isRepositioning = repositioningId === id;
              return (
                <React.Fragment key={id}>
                  <tr className={isSkipped ? "events-table__row--skipped" : ""}>
                    <td className="events-table__cell-title">{event.title}</td>
                    <td>{event.eventType || "Fizic"}</td>
                    <td>{event.location || "—"}</td>
                    <td>{event.timeInterval || "—"}</td>
                    <td>
                      {isSkipped ? (
                        <span className="events-table__badge events-table__badge--skipped">
                          Ascuns
                        </span>
                      ) : (
                        <span className="events-table__badge events-table__badge--visible">
                          Vizibil
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`events-table__btn ${isSkipped ? "events-table__btn--show" : "events-table__btn--skip"}`}
                        onClick={() => handleToggleSkip(event)}
                      >
                        {isSkipped ? "Afișează" : "Ascunde"}
                      </button>
                    </td>
                    {isAdmin && (
                      <td>
                        {event.coverImage ? (
                          <button
                            className={`events-table__btn events-table__btn--reposition${isRepositioning ? " events-table__btn--reposition-active" : ""}`}
                            onClick={() =>
                              setRepositioningId(isRepositioning ? null : id)
                            }
                            title="Repoziționează imaginea"
                          >
                            {!isRepositioning && <Move size={14} aria-hidden="true" />}
                            {isRepositioning ? "Închide" : "Poziție"}
                          </button>
                        ) : (
                          <span className="events-table__no-image">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                  {isAdmin && isRepositioning && (
                    <tr className="events-table__row--editor">
                      <td colSpan={7}>
                        <ImagePositionEditor
                          eventId={id}
                          coverImage={event.coverImage}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventsManageTable;
